
import React, { useEffect, useRef, useState } from 'react';
import { X, MapPin, Check } from 'lucide-react';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const DEFAULT_LAT = 6.927079; 
const DEFAULT_LNG = 79.861244;

let leafletLoadingPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoadingPromise) return leafletLoadingPromise;
  leafletLoadingPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', () => reject(new Error('Failed to load map library')));
      if (window.L) resolve(window.L);
      return;
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load map library'));
    document.body.appendChild(script);
  });
  return leafletLoadingPromise;
}

export default function LocationPickerMapModal({ isOpen, onClose, onSelect, initialLat, initialLng, title }) {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [picked, setPicked] = useState({
    lat: initialLat !== '' && initialLat != null && !Number.isNaN(Number(initialLat)) ? Number(initialLat) : DEFAULT_LAT,
    lng: initialLng !== '' && initialLng != null && !Number.isNaN(Number(initialLng)) ? Number(initialLng) : DEFAULT_LNG,
  });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setReady(false);
    setLoadError(false);

    const startLat = initialLat !== '' && initialLat != null && !Number.isNaN(Number(initialLat)) ? Number(initialLat) : DEFAULT_LAT;
    const startLng = initialLng !== '' && initialLng != null && !Number.isNaN(Number(initialLng)) ? Number(initialLng) : DEFAULT_LNG;
    setPicked({ lat: startLat, lng: startLng });

    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapElRef.current) return;
        const map = L.map(mapElRef.current).setView([startLat, startLng], 13);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map);

        const marker = L.marker([startLat, startLng], { draggable: true }).addTo(map);
        marker.on('dragend', () => {
          const pos = marker.getLatLng();
          setPicked({ lat: pos.lat, lng: pos.lng });
        });
        map.on('click', (e) => {
          marker.setLatLng(e.latlng);
          setPicked({ lat: e.latlng.lat, lng: e.latlng.lng });
        });

        mapRef.current = map;
        markerRef.current = marker;
        setReady(true);
        setTimeout(() => map.invalidateSize(), 150);
      })
      .catch(() => { if (!cancelled) setLoadError(true); });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 shrink-0 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
              <MapPin size={16} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm truncate">{title || 'Pick Location on Map'}</p>
              <p className="text-white/60 text-xs mt-0.5">Click anywhere on the map, or drag the pin</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors shrink-0">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 bg-gray-100 min-h-[360px] relative">
          <div ref={mapElRef} className="w-full h-full min-h-[360px]" />
          {!ready && !loadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <p className="text-xs text-gray-400">Loading map...</p>
            </div>
          )}
          {loadError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 text-center px-6">
              <p className="text-sm font-semibold text-gray-500">Couldn't load the map</p>
              <p className="text-xs text-gray-400 mt-1">Check your internet connection and try again.</p>
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-white border-t border-gray-100 flex items-center justify-between gap-3 shrink-0 flex-wrap">
          <p className="text-xs font-mono text-gray-500">{picked.lat.toFixed(6)}, {picked.lng.toFixed(6)}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs font-semibold text-gray-500 px-3 py-1.5 rounded hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={() => { onSelect(picked.lat, picked.lng); onClose(); }}
              disabled={!ready}
              className="flex items-center gap-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded transition-colors"
            >
              <Check size={12} /> Use This Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
